/* eslint-disable indent */
/* eslint-disable max-len */
import { html, css, nothing, LitElement } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    saveDraft,
    updateDraft,
    createDraftItem,
} from '../../store/draft/draft.js';
import { navigateBack } from '../../store/route/route.js';
import { previewDraft } from '../../store/preview/player.js';
import { toDuration, fromDuration } from '../../helpers/converters.js';
import { translate } from '../../helpers/translate.js';
import { isRegular, completionTypes } from '../../store/draft/types.js';
import { scrollIntoViewIfNeeded } from '../../helpers/dom.js';
import '@polymer/paper-input/paper-input.js';
import '@vaadin/checkbox/theme/material/vaadin-checkbox.js';
import '@vaadin/text-area/theme/material/vaadin-text-area.js';
import '../shared/sdb-overlay-element.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-sortable-list.js';
import '../shared/sdb-overlay-layout.js';
import '../shared/sdb-image-editor.js';
import '../shared/sdb-rich-text-editor.js';
import '../elearning/sdb-microlearning-select-dialog.js';
import '../elearning/sdb-elearning-select-dialog.js';
import './sdb-composition-draft-item.js';

@customElement('sdb-composition-draft')
class SdbCompositionDraft extends connect(store)(LitElement) {

    @property({ type: String })
    accessor draftId;

    @state()
    accessor draft;

    @state()
    accessor numItems;

    @state()
    accessor isLoading = false;

    @state()
    accessor user;

    @state()
    accessor isDirty = false;

    @state()
    accessor language;


    @query('#new_block_button')
    accessor newBlockButton;

    get singleItem() {
        return (this.draft.items || {}).length ? this.draft.items[0] : {};
    }

    stateChanged(state) {
        this.draft = state.draft?.current;
        this.numItems = ((state.draft?.current || {}).items || []).length;
        this.isLoading = state.draft?.status === 'loading' || !state.draft?.current;
        this.user = state.auth.user;
        this.isDirty = state.draft.dirty;
        this.hasGlobalId = state.env.hasGlobalId;
        this.language = state.env.language;
    };

    static get styles() {
        return [
            sharedStyles,
            css`
                [slot=content] {
                    width: 100%;
                    padding: 0 20px;
                    max-width: 1400px;
                }

                .sdb-primary-button {
                    font-family: "Source Sans Pro", sans-serif;
                    background-color: var(--btn-primary, #f49d10);
                    color: var(--sdb-button-primary-text, rgba(0,0,0,.87));
                }

                sdb-content-collapsablecard {
                }

                .column-layout {
                    margin-left: -36px;
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    align-items: flex-start;
                }

                .column-right {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    padding-left: 30px;
                }

                .form-layout {
                    margin-top: 20px;
                    display: flex;
                    flex-direction: row;
                    align-items: flex-start;
                }

                .form-layout-left {
                    display: flex;
                    width: 40%;
                }

                .form-layout-left .label {
                    padding-top: 16px;
                }

                .form-layout-right {
                    display: flex;
                    width: 60%;
                    flex-direction: column;
                }

                .row-layout {
                    width: 100%;
                    display: flex;
                }

                .row-layout > *:not(:first-child) {
                    margin-left: 20px;
                }

                vaadin-text-area {
                    width: 100%;
                }

                .medium {
                    width: 100%;
                    max-width: 700px;
                }

                .small {
                    width: 100%;
                    max-width: 300px;
                }

                .xsmall {
                    width: 100%;
                    max-width: 100px;
                }

                paper-item:not([disabled]) {
                    cursor: pointer;
                }

                div.items {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                }

                sdb-composition-draft-item {
                    margin-top: 15px;
                    width: 100%;
                }

                @keyframes zoomIn {
                    0% {
                        transform: scale(0);
                    }
                    100% {
                        transform: scale(1);
                    }
                }

                @keyframes slideDown {
                    0% {
                        top: -56px;
                    }
                    100% {
                        top: 0;
                    }
                }
            `,
        ];
    }

    render() {
        if (this.isLoading || this.draftId !== this.draft.id) {
            return html`<sdb-loading></sdb-loading>`;
        }

        const update = (changes) => store.dispatch(updateDraft({ changes }));

        return html`
            <sdb-overlay-layout
                headline=${this.draft.title}
                ?dirty=${this.isDirty}
                @back=${() => { store.dispatch(navigateBack('drafts')); }}>

                <div slot="buttons">
                    <sdb-content-button
                        id="preview_button"
                        label=${translate('preview')}
                        primary
                        ?disabled=${this.isDirty}
                        @click=${()=> { previewDraft(this.draft.id); }}>
                    </sdb-content-button>
                    <sdb-content-button
                        id="save_button"
                        label=${translate('save')}
                        primary
                        ?disabled=${!this.isDirty}
                        @click=${()=> { store.dispatch(saveDraft({ draftId: this.draft.id })); }}>
                    </sdb-content-button>
                </div>

                <div slot="content">
                    <sdb-layout-100>

                        <sdb-content-collapsablecard
                            id="general_card"
                            headline=${translate('generalSettings')}
                            icon="fal fa-cubes"
                            headlineSize="16px"
                            iconSize="32">

                            <div class="column-layout">
                                <sdb-image-editor
                                    image=${this.draft.image}
                                    draftId=${this.draft.id}
                                    iconType="elearning"
                                    @change=${(e) => { update({ image: e.detail.value }); }}>
                                </sdb-image-editor>

                                <div class="column-right">
                                    <paper-input
                                        id="title_input"
                                        transparent
                                        label=${translate('title')}
                                        value=${this.draft.title}
                                        @change=${(e) => update({ title: e.target.value })}>
                                    </paper-input>

                                    <paper-input
                                        id="display_title_input"
                                        transparent
                                        label=${translate('displayTitle')}
                                        value=${this.draft.displayTitle}
                                        @change=${(e) => update({ displayTitle: e.target.value })}>
                                    </paper-input>

                                    <vaadin-text-area
                                        id="summary_textarea"
                                        label=${translate('summary')}
                                        value=${this.draft.summary}
                                        @change=${(e) => update({ summary: e.target.value })}>
                                    </vaadin-text-area>

                                    <sdb-rich-text-editor
                                        id="description_rte"
                                        label=${translate('description')}
                                        value=${this.draft.description}
                                        language=${this.language}
                                        @change=${(e) => update({ description: e.detail.value })}>
                                    </sdb-rich-text-editor>

                                    <div class="form-layout">
                                        <div class="form-layout-left">
                                            <div class="label">${translate('completionSettings')}</div>
                                        </div>
                                        <div class="form-layout-right">
                                            ${this.hasGlobalId && isRegular(this.draft.compositionType)
                                                ? html`
                                                    <paper-input
                                                        id="min_duration_input"
                                                        transparent
                                                        class="small"
                                                        type="number"
                                                        label=${translate('requiredDuration')}
                                                        value=${fromDuration(this.draft.progressSettings.minTotalDuration)}
                                                        @change=${(e) => update({
                                                            progressSettings: {
                                                                minTotalDuration: toDuration(e.target.value)
                                                            }
                                                        })}>
                                                        <div slot="suffix">${translate('minutes')}</div>
                                                    </paper-input>
                                                ` : nothing
                                            }

                                            <div class="row-layout">
                                                <vaadin-select
                                                    id="completiontype_select"
                                                    transparent
                                                    class="medium"
                                                    label=${translate('completionType')}
                                                    value=${String(this.draft.progressSettings?.completionType || 0)}
                                                    @change=${(e) => update({
                                                        progressSettings: {
                                                            completionType : +e.target.value,
                                                        }
                                                    })}
                                                    .items=${Object.entries(completionTypes).map(([key, value]) => ({
                                                        value: String(value),
                                                        label: translate(key)
                                                    }))}>
                                                </vaadin-select>

                                                ${this.draft.progressSettings?.completionType === completionTypes.MIN_NUM_BLOCKS
                                                    ? html`
                                                        <paper-input
                                                            id="min_items_input"
                                                            transparent
                                                            class="xsmall"
                                                            type="number"
                                                            min="1"
                                                            max=${this.draft.items.length}
                                                            label=${translate('minItems')}
                                                            value=${String(this.draft.progressSettings?.minNumberItems || 1)}
                                                            @change=${(e) => update({
                                                                progressSettings: {
                                                                    minNumberItems: +e.target.value
                                                                }
                                                            })}>
                                                        </paper-input>
                                                    `: nothing
                                                }
                                            </div>

                                            <vaadin-checkbox
                                                id="surpress_final_score_checkbox"
                                                style="margin-top: 12px;"
                                                label=${translate('surpressFinalScore')}
                                                ?checked=${this.draft.progressSettings?.surpressFinalScore}
                                                @checked-changed=${(e) => update({
                                                    progressSettings: {
                                                        surpressFinalScore: e.target.checked
                                                    }
                                                })}>
                                            </vaadin-checkbox>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </sdb-content-collapsablecard>

                        ${isRegular(this.draft.compositionType)
                            ? html`
                                <div class="items">
                                    ${this.draft.items ? html`
                                        ${map(this.draft.items, (item, index) => html`
                                            <sdb-composition-draft-item
                                                .item=${item}
                                                .isFirst=${index === 0}
                                                .isLast=${index === this.draft.items.length - 1}>
                                            </sdb-composition-draft-item>
                                        `)}` : nothing
                                    }

                                    <sdb-layout-filler size="15"></sdb-layout-filler>
                                    <sdb-content-button
                                        id="new_block_button"
                                        label=${translate('newBlock')}
                                        secondary
                                        @click=${() => { store.dispatch(createDraftItem()); }}>
                                    </sdb-content-button>
                                </div>
                            ` : nothing
                        }

                    </sdb-layout-100>
                </div>
            </sdb-overlay-layout>

            <sdb-microlearning-select-dialog></sdb-microlearning-select-dialog>
            <sdb-elearning-select-dialog></sdb-elearning-select-dialog>
        `;
    }

    firstUpdated() {
        this.dispatchEvent(
            new CustomEvent('fullscreen', {
                bubbles: true,
                composed: true,
                detail: {
                    fullscreen: true,
                },
            }),
        );
    }

    updated(changes) {
        if (!changes.get('numItems')) {
            return;
        }

        const oldValue = parseInt(changes.get('numItems'), 10);

        if (this.numItems > oldValue) {
            setTimeout(() => {
                scrollIntoViewIfNeeded(this.newBlockButton);
            }, 0);
        }
    }
}

